import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  ConnectedBadge,
  IntegrationAccountCard,
  IntegrationProviderHeader,
} from '@/components/integrations/IntegrationCards';
import { useOrg } from '@/contexts/OrgContext';
import { Button } from '@/lib/base/buttons/button';
import { ConfirmationDialog } from '@/lib/base/overlays';
import { logger } from '@/lib/logger';
import { useViewHealthMarker } from '@/lib/view-health';

import {
  useIssueWhatsAppLink,
  useUnlinkWhatsApp,
  useWhatsAppStatus,
  whatsappStatusQueryKey,
} from './hooks';
import type { WhatsAppStatus } from './types';
import { WhatsAppIcon } from './WhatsAppIcon';

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? '—'
    : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function rePointDisclosure(orgName: string | undefined): string {
  return `Linking moves your WhatsApp number to act on the ${orgName ?? 'this'} workspace. If it's already linked to another workspace, it will be moved here.`;
}

export function WhatsAppSection() {
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id ?? 0;
  const orgName = currentOrg?.name;

  const issue = useIssueWhatsAppLink(orgId);
  const unlink = useUnlinkWhatsApp(orgId);

  // Poll only in the window between "code issued" and "linked confirmed" so
  // the section detects verification live, without a page reload, then stop
  // the instant either resolves. The gate needs the status query's own
  // cached `linked` value read up front (via the query cache directly,
  // since the value can't come from the `status` query object itself —
  // that's the call we're configuring).
  const queryClient = useQueryClient();
  const cachedStatus = queryClient.getQueryData<WhatsAppStatus>(
    whatsappStatusQueryKey(orgId)
  );
  const pendingVerification =
    Boolean(issue.data) && cachedStatus?.linked !== true;
  const status = useWhatsAppStatus(orgId, {
    pollForVerification: pendingVerification,
  });
  const { refetch } = status;

  useViewHealthMarker('settings-section-ready', { ready: true });

  const [isUnlinkConfirmOpen, setIsUnlinkConfirmOpen] = useState(false);

  const actionErrorRef = useRef<HTMLDivElement>(null);
  const isActionError = issue.isError || unlink.isError;
  const actionError = issue.error ?? unlink.error;

  useEffect(() => {
    if (isActionError) {
      actionErrorRef.current?.focus();
      logger.error(
        'WhatsApp action failed',
        actionError instanceof Error
          ? actionError
          : new Error(String(actionError))
      );
    }
  }, [isActionError, actionError]);

  useEffect(() => {
    if (status.data?.linked) {
      issue.reset();
    }
  }, [status.data?.linked, issue]);

  const handleRetryStatus = useCallback(() => {
    void refetch?.();
  }, [refetch]);

  const handleIssueLink = useCallback(() => {
    issue.mutate();
  }, [issue]);

  const handleOpenUnlinkConfirm = useCallback(() => {
    setIsUnlinkConfirmOpen(true);
  }, []);

  const handleCancelUnlink = useCallback(() => {
    setIsUnlinkConfirmOpen(false);
  }, []);

  const handleConfirmUnlink = useCallback(() => {
    unlink.mutate(undefined, {
      onSuccess: () => {
        setIsUnlinkConfirmOpen(false);
        issue.reset();
      },
    });
  }, [unlink, issue]);

  const statusData = status.data;
  let actionErrorMessage: string | null = null;
  if (isActionError) {
    actionErrorMessage = issue.isError
      ? "We couldn't link WhatsApp. Try again."
      : "We couldn't unlink WhatsApp. Try again.";
  }

  return (
    <div className='space-y-8'>
      <header>
        <IntegrationProviderHeader
          logo={<WhatsAppIcon className='w-5 h-5' />}
          title='WhatsApp'
          subtitle='Message Ahoy from WhatsApp. Link your phone to this workspace.'
        />
      </header>

      <div className='space-y-6'>
        {isActionError && (
          <div
            ref={actionErrorRef}
            role='alert'
            tabIndex={-1}
            className='rounded-lg border border-utility-error-200 bg-utility-error-50 p-4'
          >
            <p className='text-sm text-utility-error-700'>
              {actionErrorMessage}
            </p>
          </div>
        )}

        {status.isLoading && !statusData && (
          <div role='status' className='text-sm text-fg-tertiary'>
            Loading…
          </div>
        )}

        {status.isError && statusData === undefined && (
          <div
            role='alert'
            className='rounded-lg border border-utility-error-200 bg-utility-error-50 p-4'
          >
            <p className='text-sm text-utility-error-700'>
              We couldn&apos;t load your WhatsApp connection.
            </p>
            <Button
              color='secondary'
              size='sm'
              className='mt-3'
              onClick={handleRetryStatus}
            >
              Retry
            </Button>
          </div>
        )}

        {statusData?.linked === true && (
          <IntegrationAccountCard
            logo={<WhatsAppIcon className='w-5 h-5' />}
            email={statusData.masked_phone}
            subLabel={`Verified ${formatDateTime(statusData.verified_at)}`}
            badges={<ConnectedBadge />}
            actions={
              <Button
                color='secondary-destructive'
                size='sm'
                onClick={handleOpenUnlinkConfirm}
                isDisabled={unlink.isPending}
              >
                Unlink
              </Button>
            }
          >
            <div className='border-t border-border-secondary px-4 py-3'>
              <p className='text-sm text-fg-secondary'>
                Acts on the {orgName} workspace.
              </p>
            </div>
          </IntegrationAccountCard>
        )}

        {issue.data && statusData?.linked !== true && (
          <div className='max-w-2xl space-y-4 rounded-xl border border-border-secondary p-6'>
            <p className='text-sm text-fg-secondary'>
              Open WhatsApp and send this message so we can confirm it&apos;s
              you. This code expires soon, so send it right away.
            </p>
            <p className='mp-mask text-lg font-semibold text-fg-primary'>
              {issue.data.code}
            </p>
            <Button
              href={issue.data.wa_me_url}
              target='_blank'
              rel='noopener noreferrer'
              color='primary'
              className='mp-mask'
            >
              Open WhatsApp to send your code (opens in a new tab)
            </Button>
            <p className='text-sm text-fg-secondary'>
              Prefer to send it yourself? Message the code to{' '}
              {issue.data.platform_number} in WhatsApp.
            </p>
            <p className='text-xs text-fg-tertiary'>
              This code is single use and expires soon.
            </p>
            <p className='text-xs text-fg-tertiary'>
              {rePointDisclosure(orgName)}
            </p>
          </div>
        )}

        {statusData?.linked === false && !issue.data && (
          <div className='max-w-2xl space-y-4 rounded-xl border border-border-secondary p-6'>
            <p className='text-sm text-fg-secondary'>
              Message Ahoy on WhatsApp to catch up, ask questions, or take quick
              actions – right from your phone.
            </p>
            <p className='text-xs text-fg-tertiary'>
              {rePointDisclosure(orgName)}
            </p>
            <Button
              color='primary'
              size='sm'
              onClick={handleIssueLink}
              isDisabled={issue.isPending}
            >
              Link WhatsApp
            </Button>
          </div>
        )}
      </div>

      <ConfirmationDialog
        isOpen={isUnlinkConfirmOpen}
        variant='destructive'
        title='Unlink WhatsApp'
        description='Ahoy will stop responding to messages from this WhatsApp number. You can link it again any time.'
        confirmLabel='Unlink'
        isLoading={unlink.isPending}
        instance='unlink-whatsapp'
        onConfirm={handleConfirmUnlink}
        onCancel={handleCancelUnlink}
      />
    </div>
  );
}
